import { Menu, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ctaByRoute, organization } from "../../content/config";
import { isLocale, localizedPath, pathWithoutLocale } from "../../lib/routes";
import { Button } from "../ui/Button";
import { LanguageSwitcher } from "../navigation/LanguageSwitcher";

const navItems = [
  ["nav.moldova", "moldova"],
  ["nav.israel", "israel"],
  ["nav.programs", "programs"],
  ["nav.insights", "insights"],
  ["nav.about", "about"]
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const { locale } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const activeLocale = isLocale(locale) ? locale : "en";

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const cta = useMemo(() => {
    const path = location.pathname.replace(`/${activeLocale}`, "");
    return ctaByRoute.find((item) => path.startsWith(item.match)) ?? { label: t("nav.start"), flow: "generic" };
  }, [activeLocale, location.pathname, t]);

  const route = pathWithoutLocale(location.pathname);
  const onMoldova = route === "/moldova";
  const onIsrael = route === "/israel";
  const scrollToIsraelFit = () => {
    document.getElementById("moldova-fit")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition ${solid || open ? "border-b border-line bg-bg-primary/95 backdrop-blur" : "bg-transparent"}`}>
      <div className="container-grid flex h-[var(--header-height)] items-center justify-between gap-4">
        <Link to={localizedPath(activeLocale)} className="flex min-w-0 shrink-0 items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-primary">
          <span className="grid h-11 w-11 shrink-0 place-items-center">
            <img src="/branding/transition-bridge-mark.png" alt="Transition Bridge logo" width={44} height={44} className="h-11 w-11 object-contain" />
          </span>
          <span className="hidden min-w-0 sm:block">
            <span className="block truncate whitespace-nowrap font-display text-sm font-extrabold tracking-tight">{organization.brandName}</span>
            <span className="hidden truncate text-xs text-ink-secondary xl:block">
              {organization.registrationStatus === "registered" ? organization.legalNameEn : organization.publicDescriptor}
            </span>
          </span>
        </Link>

        <nav className="hidden min-w-0 items-center justify-center gap-0.5 lg:flex xl:gap-1" aria-label="Main navigation">
          {navItems.map(([label, path]) => (
            <Link key={path} className="rounded-lg px-2.5 py-2 text-sm font-semibold text-ink-secondary transition hover:bg-surface hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-primary xl:px-3" to={localizedPath(activeLocale, path)}>
              {t(label)}
            </Link>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          <LanguageSwitcher />
          {onMoldova && cta.flow === "moldova" ? (
            <Button to={localizedPath(activeLocale, "moldova/screening")} calm>{cta.label}</Button>
          ) : onIsrael && cta.flow === "israel" ? (
            <Button type="button" calm onClick={scrollToIsraelFit}>{cta.label}</Button>
          ) : (
            <Button to={localizedPath(activeLocale, `contact?flow=${cta.flow}`)} calm>{cta.label}</Button>
          )}
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button className="grid h-11 w-11 place-items-center rounded-lg border border-line bg-surface transition hover:border-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-primary" type="button" onClick={() => setOpen((value) => !value)} aria-label={open ? t("nav.close") : t("nav.menu")} aria-expanded={open}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-line bg-bg-primary lg:hidden">
          <nav className="container-grid grid gap-2 py-6" aria-label="Mobile navigation">
            {navItems.map(([label, path]) => (
              <Link key={path} className="rounded-lg border-b border-line py-4 text-xl font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-primary" to={localizedPath(activeLocale, path)}>
                {t(label)}
              </Link>
            ))}
            <Button className="mt-4 w-full" to={localizedPath(activeLocale, `contact?flow=${cta.flow}`)}>{cta.label}</Button>
          </nav>
        </div>
      )}
    </header>
  );
}
